/**
 * Membership 5787 — fallback/config values that are NOT sourced from the
 * backend today but are called out explicitly in the client's build notes
 * as "configurable, not hardcoded." The tier prices themselves always come
 * from GET /api/membership/seat-lock-info and GET /api/membership/tiers —
 * these constants only cover the High Holiday seat pricing, which the
 * agreed API contract (tiers + seat-lock-info) has no field for yet.
 *
 * getSeatPricing() below is the ONE place that decides where seat prices
 * come from, so wiring in a real backend field later is a one-line change
 * here rather than a hunt through the components.
 */

// Seats included per household regardless of tier (husband + wife).
export const DEFAULT_INCLUDED_SEATS = 2;

// Member rate for each seat beyond the included 2 — applies to every paid
// tier AND to First-Year Couples (who pay the member rate even though
// their membership itself is free).
export const DEFAULT_MEMBER_EXTRA_SEAT_PRICE = 100;

// Non-member (guest) rate per seat — client-confirmed (Aug 2026, final)
// at $360, deliberately well above the $100 member rate so the savings
// from joining are obvious. History: wireframe proposed $200, briefly set
// to $360, corrected to $100 (same as members), now locked at $360.
// Treated as a default only, overridden by whatever the backend returns
// (see getSeatPricing).
export const DEFAULT_NON_MEMBER_EXTRA_SEAT_PRICE = 360;

// Reads seat-pricing fields off the /api/membership/seat-lock-info payload
// if the backend has started including them, otherwise falls back to the
// defaults above. Keeping this indirection means the frontend never
// hardcodes a price in a component — it always asks this function first.
export const getSeatPricing = (seatLockInfo) => ({
  includedSeats: seatLockInfo?.includedSeats ?? DEFAULT_INCLUDED_SEATS,
  memberExtraSeatPrice:
    seatLockInfo?.memberSeatPrice ?? DEFAULT_MEMBER_EXTRA_SEAT_PRICE,
  nonMemberExtraSeatPrice:
    seatLockInfo?.nonMemberSeatPrice ?? DEFAULT_NON_MEMBER_EXTRA_SEAT_PRICE,
});
